import { useState, useEffect } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import { CREATE_OPTION_ITEM, UPDATE_OPTION_ITEM } from '../graphql/mutations'
import { GET_OPTION_ITEMS, GET_OPTION_GROUPS, GET_PRODUCTS } from '../graphql/product-queries'
import { CreateOptionItemData, UpdateOptionItemData, OptionItem } from '../types/product'
import toast from 'react-hot-toast'

interface OptionItemFormProps {
  optionItem?: OptionItem
  isEditing?: boolean
  defaultGroupId?: number
}

const OptionItemForm = ({ optionItem, isEditing = false, defaultGroupId }: OptionItemFormProps) => {
  const navigate = useNavigate()
  
  const [formData, setFormData] = useState<CreateOptionItemData>({
    option_group_id: defaultGroupId || 0,
    label: '',
    product_id: null,
    price_delta_cents: 0,
    default_selected: false,
    sort_order: null,
    effect_json: null,
    image_url: ''
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [effectJsonString, setEffectJsonString] = useState('')
  const [imagePreviewFailed, setImagePreviewFailed] = useState(false)

  const { data: optionGroupsData } = useQuery(GET_OPTION_GROUPS)
  const { data: productsData } = useQuery(GET_PRODUCTS)

  const [createOptionItem, { loading: createLoading }] = useMutation(CREATE_OPTION_ITEM, {
    onCompleted: () => {
      toast.success('Option item created successfully')
      navigate('/option-items')
    },
    onError: (error) => {
      toast.error(`Failed to create option item: ${error.message}`)
      if (error.message.includes('already exists')) {
        setErrors({ label: 'An option item with this label already exists in this group' })
      }
    }
  })

  const [updateOptionItem, { loading: updateLoading }] = useMutation(UPDATE_OPTION_ITEM, {
    onCompleted: () => {
      toast.success('Option item updated successfully')
      navigate('/option-items')
    },
    onError: (error) => {
      toast.error(`Failed to update option item: ${error.message}`)
      if (error.message.includes('already exists')) {
        setErrors({ label: 'An option item with this label already exists in this group' })
      }
    }
  })

  useEffect(() => {
    if (optionItem && isEditing) {
      setFormData({
        option_group_id: optionItem.option_group_id,
        label: optionItem.label,
        product_id: optionItem.product_id,
        price_delta_cents: optionItem.price_delta_cents,
        default_selected: optionItem.default_selected,
        sort_order: optionItem.sort_order,
        effect_json: optionItem.effect_json,
        image_url: optionItem.image_url || ''
      })
      setEffectJsonString(optionItem.effect_json ? JSON.stringify(optionItem.effect_json, null, 2) : '')
    }
  }, [optionItem, isEditing])

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.label.trim()) {
      newErrors.label = 'Label is required'
    }

    if (!formData.option_group_id || formData.option_group_id <= 0) {
      newErrors.option_group_id = 'Option group is required'
    }

    // Validate effect_json if provided
    if (effectJsonString.trim()) {
      try {
        JSON.parse(effectJsonString)
      } catch (error) {
        newErrors.effect_json = 'Invalid JSON format'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    try {
      const submitData = {
        ...formData,
        // Ensure numeric fields are properly typed as integers
        option_group_id: Number(formData.option_group_id),
        product_id: formData.product_id !== null && formData.product_id !== undefined
          ? Number(formData.product_id)
          : null,
        price_delta_cents: Number(formData.price_delta_cents || 0),
        sort_order: formData.sort_order !== null && formData.sort_order !== undefined
          ? Number(formData.sort_order)
          : null,
        effect_json: effectJsonString.trim() ? JSON.parse(effectJsonString) : null,
        // Explicit null clears the field; the API stores "" verbatim, so never send it.
        image_url: formData.image_url?.trim() || null
      }

      if (isEditing && optionItem) {
        const updateData: UpdateOptionItemData = { ...submitData }
        await updateOptionItem({
          variables: {
            id: Number(optionItem.id),
            data: updateData
          },
          refetchQueries: [GET_OPTION_ITEMS]
        })
      } else {
        await createOptionItem({
          variables: { data: submitData },
          refetchQueries: [GET_OPTION_ITEMS]
        })
      }
    } catch (error) {
      // Error handling is done in the mutation callbacks
    }
  }

  const handleInputChange = (field: keyof CreateOptionItemData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (field === 'image_url') {
      setImagePreviewFailed(false)
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleEffectJsonChange = (value: string) => {
    setEffectJsonString(value)
    if (errors.effect_json) {
      setErrors(prev => ({ ...prev, effect_json: '' }))
    }
  }

  const loading = createLoading || updateLoading
  const optionGroups = optionGroupsData?.optionGroups || []
  const products = productsData?.products || []

  return (
    <div className="max-w-2xl mx-auto">
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">
            {isEditing ? 'Edit Option Item' : 'Create Option Item'}
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {isEditing 
              ? 'Update the option item settings below'
              : 'Configure the option item for product customization'
            }
          </p>
        </div>

        {/* noValidate: validation runs in validateForm, and native URL checking
            would block saving items whose stored image_url lacks a scheme */}
        <form onSubmit={handleSubmit} noValidate className="p-6 space-y-6">
          {/* Option Group */}
          <div>
            <label htmlFor="option_group_id" className="block text-sm font-medium text-gray-700 mb-2">
              Option Group *
            </label>
            <select
              id="option_group_id"
              value={formData.option_group_id}
              onChange={(e) => handleInputChange('option_group_id', parseInt(e.target.value))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.option_group_id ? 'border-red-300' : 'border-gray-300'
              }`}
              disabled={loading}
            >
              <option value={0}>Select an option group</option>
              {optionGroups.map((group: any) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
            {errors.option_group_id && (
              <p className="mt-1 text-sm text-red-600">{errors.option_group_id}</p>
            )}
          </div>

          {/* Label */}
          <div>
            <label htmlFor="label" className="block text-sm font-medium text-gray-700 mb-2">
              Label *
            </label>
            <input
              type="text"
              id="label"
              value={formData.label}
              onChange={(e) => handleInputChange('label', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.label ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="e.g., Normal Coriander"
              disabled={loading}
            />
            {errors.label && (
              <p className="mt-1 text-sm text-red-600">{errors.label}</p>
            )}
          </div>

          {/* Product */}
          <div>
            <label htmlFor="product_id" className="block text-sm font-medium text-gray-700 mb-2">
              Linked Product
            </label>
            <select
              id="product_id"
              value={formData.product_id || ''}
              onChange={(e) => handleInputChange('product_id', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={loading}
            >
              <option value="">No linked product</option>
              {products.map((product: any) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">Optional. A linked item follows that product's sold-out state (greyed in the wizards, refused at checkout), and the product cannot be deleted while the link exists.</p>
          </div>

          {/* Image URL */}
          <div>
            <label htmlFor="image_url" className="block text-sm font-medium text-gray-700 mb-2">
              Image URL
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="url"
                id="image_url"
                value={formData.image_url || ''}
                onChange={(e) => handleInputChange('image_url', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://storage.googleapis.com/curry_me_public_images/..."
                disabled={loading}
              />
              {formData.image_url && !imagePreviewFailed && (
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="h-12 w-12 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                  onError={() => setImagePreviewFailed(true)}
                />
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">Optional - shown on the kiosk customization screen</p>
          </div>

          {/* Price Delta */}
          <div>
            <label htmlFor="price_delta_cents" className="block text-sm font-medium text-gray-700 mb-2">
              Price Delta (cents)
            </label>
            <input
              type="number"
              id="price_delta_cents"
              value={formData.price_delta_cents}
              onChange={(e) => handleInputChange('price_delta_cents', parseInt(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="0"
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500">Additional cost in cents (can be negative for discounts)</p>
          </div>

          {/* Sort Order */}
          <div>
            <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700 mb-2">
              Sort Order
            </label>
            <input
              type="number"
              id="sort_order"
              value={formData.sort_order || ''}
              onChange={(e) => handleInputChange('sort_order', e.target.value ? parseInt(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Optional - lower numbers appear first"
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500">Optional - controls display order within the group</p>
          </div>

          {/* Effect JSON */}
          <div>
            <label htmlFor="effect_json" className="block text-sm font-medium text-gray-700 mb-2">
              Effect JSON
            </label>
            <textarea
              id="effect_json"
              value={effectJsonString}
              onChange={(e) => handleEffectJsonChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm ${
                errors.effect_json ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder='{"dietary": "normal_coriander", "allergen_free": false}'
              rows={4}
              disabled={loading}
            />
            {errors.effect_json && (
              <p className="mt-1 text-sm text-red-600">{errors.effect_json}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">Optional - JSON for dietary/allergen information</p>
          </div>

          {/* Default Selected */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="default_selected"
              checked={formData.default_selected}
              onChange={(e) => handleInputChange('default_selected', e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              disabled={loading}
            />
            <label htmlFor="default_selected" className="ml-2 block text-sm text-gray-700">
              Default Selected
            </label>
          </div>
          <p className="ml-6 text-xs text-gray-500">
            Only one option item per group can be the default
          </p>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/option-items')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </div>
              ) : (
                isEditing ? 'Update Option Item' : 'Create Option Item'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default OptionItemForm
